import { useRef, useState } from "react";
import EntityManager from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Printer, Download, Paperclip, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { BRANCHES } from "@/lib/branches";
import { fmtDate, today } from "@/lib/format";
import { printLogBook } from "@/lib/print";

const RECORD_TYPES = ["Audit Record", "Business Record"];
const typeCls = {
  "Audit Record": "bg-indigo-50 text-indigo-700",
  "Business Record": "bg-emerald-50 text-emerald-700",
};

const csvEsc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const exportLogBookExcel = (rows) => {
  const lines = ["Sl,Branch,Date,Record Type,Subject / Title,Details,Remarks,Follow-up Date,Recorded By,Proof Attached"];
  rows
    .slice()
    .sort((a, b) => String(a.branch || "").localeCompare(String(b.branch || "")) || String(a.date || "").localeCompare(String(b.date || "")))
    .forEach((r, i) => {
      lines.push([i + 1, csvEsc(r.branch || "Chennai"), csvEsc(r.date ? fmtDate(r.date) : ""), csvEsc(r.record_type || "Audit Record"), csvEsc(r.title), csvEsc(r.details), csvEsc(r.remarks), csvEsc(r.follow_up_date ? fmtDate(r.follow_up_date) : ""), csvEsc(r.recorded_by), r.attachment_path ? "Yes" : "No"].join(","));
    });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  a.download = "log-book.csv";
  a.click();
};

export default function LogBook() {
  const loadRef = useRef(null);
  const fileRef = useRef(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [busy, setBusy] = useState("");

  const pickFile = (r) => {
    setUploadFor(r);
    fileRef.current && fileRef.current.click();
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !uploadFor) return;
    setBusy(uploadFor.id);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/logbook/${uploadFor.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Proof attached to "${uploadFor.title}"`);
      loadRef.current && loadRef.current();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
      setUploadFor(null);
    }
  };
  const removeAttachment = async (r) => {
    if (!window.confirm(`Remove proof from "${r.title}"?`)) return;
    setBusy(r.id);
    try {
      await api.delete(`/logbook/${r.id}/attachment`);
      toast.success("Proof removed");
      loadRef.current && loadRef.current();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={onFile} data-testid="logbook-file-input" />
      <EntityManager
        title="NTC Log Book"
        subtitle="Branch-wise register for audit records and business records."
        endpoint="logbook"
        entityName="Log Entry"
        headerExtra={(rows, displayRows, filterVals, load) => {
          loadRef.current = load;
          const branchSel = filterVals?.branch || "All Branches";
          const list = displayRows || rows;
          const downloadPack = async () => {
            try {
              const res = await api.get("/logbook/audit-pack", { params: branchSel !== "All Branches" ? { branch: branchSel } : {}, responseType: "blob" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(res.data);
              a.download = `audit-pack-${branchSel.toLowerCase().replace(/\s+/g, "-")}.zip`;
              a.click();
              toast.success("Audit pack downloaded — register + proof files");
            } catch {
              toast.error("Audit pack download failed");
            }
          };
          return (
            <>
              <Button variant="outline" disabled={!list.length} onClick={() => printLogBook(list, branchSel)} data-testid="print-log-book-btn">
                <Printer className="mr-2 h-4 w-4" /> {branchSel !== "All Branches" ? `Print ${branchSel} Log` : "Print NTC Log Book"}
              </Button>
              <Button variant="outline" disabled={!list.length} onClick={() => exportLogBookExcel(list)} data-testid="logbook-excel-btn">
                <Download className="mr-2 h-4 w-4" /> Excel
              </Button>
              <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" disabled={!list.length} onClick={downloadPack} data-testid="audit-pack-btn">
                <Download className="mr-2 h-4 w-4" /> Audit Pack
              </Button>
            </>
          );
        }}
        filters={[
          {
            key: "q",
            label: "Search",
            type: "text",
            placeholder: "Title / details / remarks",
            match: (r, v) => {
              const q = String(v || "").trim().toLowerCase();
              if (!q) return true;
              return [r.title, r.details, r.remarks, r.branch, r.record_type, r.recorded_by].some((x) => String(x || "").toLowerCase().includes(q));
            },
          },
          { key: "branch", label: "Branch", type: "select", options: ["All Branches", ...BRANCHES], match: (r, v) => !v || v === "All Branches" || (r.branch || "Chennai") === v },
          { key: "record_type", label: "Record Type", type: "select", options: ["All", ...RECORD_TYPES], match: (r, v) => !v || v === "All" || (r.record_type || "Audit Record") === v },
        ]}
        defaults={{ date: today(), branch: "Chennai", record_type: "Audit Record", title: "", details: "", remarks: "" }}
        fields={[
          { key: "date", label: "Date", type: "date" },
          { key: "branch", label: "Branch", type: "select", options: BRANCHES },
          { key: "record_type", label: "Record Type", type: "select", options: RECORD_TYPES },
          { key: "title", label: "Subject / Title", type: "text", placeholder: "e.g. Internal audit of cash book" },
          { key: "details", label: "Details", type: "textarea", placeholder: "What was checked / recorded, findings, references…" },
          { key: "remarks", label: "Remarks", type: "text", placeholder: "e.g. Closed, Pending follow-up" },
          { key: "follow_up_date", label: "Follow-up Date (for pending entries)", type: "date" },
        ]}
        columns={[
          { key: "sl_no", label: "Sl No.", render: (r, i) => i + 1 },
          { key: "branch", label: "Branch", render: (r) => r.branch || "Chennai" },
          { key: "date", label: "Date", render: (r) => (r.date ? fmtDate(r.date) : "—") },
          {
            key: "record_type",
            label: "Record Type",
            render: (r) => (
              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${typeCls[r.record_type] || "bg-slate-100 text-slate-600"}`} data-testid="logbook-type-chip">
                {r.record_type || "Audit Record"}
              </span>
            ),
          },
          { key: "title", label: "Subject / Title" },
          { key: "details", label: "Details", render: (r) => <span className="block max-w-[280px] truncate" title={r.details}>{r.details || "—"}</span> },
          { key: "remarks", label: "Remarks", render: (r) => r.remarks || "—" },
          {
            key: "follow_up_date",
            label: "Follow-up",
            render: (r) => {
              if (!r.follow_up_date) return "—";
              const due = String(r.follow_up_date).slice(0, 10) <= today() && String(r.remarks || "").toLowerCase().includes("pending");
              return (
                <span className={due ? "inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700" : ""} data-testid="logbook-followup-cell">
                  {fmtDate(r.follow_up_date)}{due ? " · DUE" : ""}
                </span>
              );
            },
          },
          { key: "recorded_by", label: "Recorded By", render: (r) => r.recorded_by || "—" },
          {
            key: "_proof",
            label: "Proof",
            render: (r) =>
              r.attachment_path ? (
                <span className="inline-flex items-center gap-1">
                  <a
                    href={`${API}/files/${r.attachment_path}?auth=${localStorage.getItem("ntc_token")}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 hover:bg-emerald-100"
                    title={r.attachment_filename || "View proof"}
                    data-testid="logbook-proof-view"
                  >
                    <Eye className="h-3 w-3" /> View
                  </a>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeAttachment(r); }}
                    disabled={busy === r.id}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title="Remove proof"
                    data-testid="logbook-proof-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); pickFile(r); }}
                  disabled={busy === r.id}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-slate-500 hover:bg-slate-100"
                  title="Attach photo / PDF proof"
                  data-testid="logbook-proof-attach"
                >
                  <Paperclip className="h-3 w-3" /> {busy === r.id ? "Uploading…" : "Attach"}
                </button>
              ),
          },
        ]}
      />
    </>
  );
}
